import { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Bell, CheckCheck } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { PageHeader } from "@/components/shared";
import DataTablePagination from "@/components/shared/DataTable/DataTablePagination";
import { cn } from "@/lib/utils";
import {
  useMarkAllNotificationsReadMutation,
  useMarkNotificationReadMutation,
  useNotifications,
} from "@/hooks/queries";
import type { NotificationDto } from "@/types/notification.dto";
import type { NotificationScope } from "@/components/shared/notifications/NotificationBell";
import {
  formatNotificationRelativeTime,
  getNotificationColor,
  getNotificationIcon,
  getNotificationTypeLabel,
} from "@/lib/notification-utils";

interface PanelNotificationsPageProps {
  scope: NotificationScope;
}

type ReadFilter = "all" | "unread";

const PanelNotificationsPage = ({ scope }: PanelNotificationsPageProps) => {
  const navigate = useNavigate();
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [readFilter, setReadFilter] = useState<ReadFilter>("all");

  const filter = useMemo(
    () => ({
      page,
      pageSize,
      ...(readFilter === "unread" ? { isRead: false } : {}),
    }),
    [page, pageSize, readFilter],
  );

  const { data, isLoading } = useNotifications(scope, filter);
  const markRead = useMarkNotificationReadMutation();
  const markAllRead = useMarkAllNotificationsReadMutation();

  const notifications = data?.items ?? [];
  const unreadOnPage = notifications.filter((notification) => !notification.isRead).length;

  const pagination = {
    page: data?.page ?? page,
    pageSize: data?.pageSize ?? pageSize,
    totalCount: data?.totalItems ?? 0,
    totalPages: data?.totalPages ?? 0,
    from: data && data.totalItems > 0 ? (data.page - 1) * data.pageSize + 1 : 0,
    to: data ? Math.min(data.page * data.pageSize, data.totalItems) : 0,
  };

  const handleFilterChange = (value: ReadFilter) => {
    setReadFilter(value);
    setPage(1);
  };

  const handleNotificationClick = (notification: NotificationDto) => {
    if (!notification.isRead) {
      markRead.mutate(notification.id);
    }
    if (notification.actionUrl) {
      navigate(notification.actionUrl);
    }
  };

  return (
    <div className="space-y-6">
      <PageHeader
        title="Notifications"
        description="Stay updated on orders, approvals, payouts, and account activity."
        actions={
          unreadOnPage > 0 || readFilter === "unread" ? (
            <Button
              variant="outline"
              size="sm"
              className="gap-2"
              onClick={() => markAllRead.mutate()}
              disabled={markAllRead.isPending}
            >
              <CheckCheck className="h-4 w-4" />
              Mark all read
            </Button>
          ) : null
        }
      />

      <Tabs value={readFilter} onValueChange={(value) => handleFilterChange(value as ReadFilter)}>
        <TabsList>
          <TabsTrigger value="all">All</TabsTrigger>
          <TabsTrigger value="unread">Unread</TabsTrigger>
        </TabsList>
      </Tabs>

      {isLoading ? (
        <div className="space-y-3">
          {Array.from({ length: 5 }).map((_, index) => (
            <Skeleton key={index} className="h-24 w-full rounded-xl" />
          ))}
        </div>
      ) : notifications.length === 0 ? (
        <Card>
          <CardContent className="flex flex-col items-center justify-center gap-3 py-16 text-center">
            <div className="flex h-14 w-14 items-center justify-center rounded-full bg-secondary">
              <Bell className="h-7 w-7 text-muted-foreground" />
            </div>
            <div>
              <p className="font-semibold text-foreground">No notifications yet</p>
              <p className="mt-1 text-sm text-muted-foreground">
                {readFilter === "unread"
                  ? "You're all caught up. No unread notifications."
                  : "When something important happens, it will show up here."}
              </p>
            </div>
          </CardContent>
        </Card>
      ) : (
        <Card className="overflow-hidden">
          <div className="divide-y divide-border">
            {notifications.map((notification) => {
              const Icon = getNotificationIcon(notification.type);
              return (
                <button
                  key={notification.id}
                  type="button"
                  className={cn(
                    "flex w-full items-start gap-4 px-4 py-4 text-left transition-colors hover:bg-secondary/30 sm:px-6",
                    !notification.isRead && "bg-primary/[0.03]",
                  )}
                  onClick={() => handleNotificationClick(notification)}
                >
                  <div
                    className={cn(
                      "mt-0.5 flex h-10 w-10 shrink-0 items-center justify-center rounded-xl",
                      getNotificationColor(notification.type),
                    )}
                  >
                    <Icon className="h-4 w-4" />
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="flex flex-wrap items-center gap-2">
                      <p
                        className={cn(
                          "text-sm sm:text-base",
                          !notification.isRead ? "font-semibold text-foreground" : "font-medium text-muted-foreground",
                        )}
                      >
                        {notification.title}
                      </p>
                      <Badge variant="outline" className="text-[10px]">
                        {notification.typeText || getNotificationTypeLabel(notification.type)}
                      </Badge>
                      {!notification.isRead && (
                        <Badge className="bg-primary/10 text-[10px] text-primary hover:bg-primary/10">New</Badge>
                      )}
                    </div>
                    <p className="mt-1 text-sm text-muted-foreground">{notification.body}</p>
                    <p className="mt-2 text-xs font-medium text-muted-foreground">
                      {formatNotificationRelativeTime(notification.createdAt)}
                    </p>
                  </div>
                </button>
              );
            })}
          </div>

          <DataTablePagination
            pagination={pagination}
            onPageChange={setPage}
            onPageSizeChange={(size) => {
              setPageSize(size);
              setPage(1);
            }}
          />
        </Card>
      )}
    </div>
  );
};

export default PanelNotificationsPage;
